import { useEffect, useReducer, useState } from 'react'

// ─── Async failure detection ──────────────────────────────────────────────
//
// React error boundaries and the synchronous probe miss two failure modes
// that show up on real devices:
//
//   1. Unhandled Promise rejections from luma.gl's `_initDevice` /
//      `createBrowserContext` — they reject *after* the component mounts.
//   2. Synchronous throws from `ResizeObserver` callbacks
//      (`getMaxDrawingBufferSize` reading `maxTextureDimension2D` on an
//      undefined adapterInfo) — these run outside React's render tree.
//
// We install one set of window-level listeners at module load, sniff the
// stack trace for luma.gl-attributable strings, and flip a session-wide
// "fatal" flag on the first hit. Every `useWebGLSupport()` consumer is
// subscribed and re-renders to pick up the new value, which cascades through
// `WebGLMarkerSurface` → DOM fallback for the rest of the session.
//
// State (failure flag + subscriber set + listener-installed flag) lives on
// `window` rather than module-locals so CRA Fast Refresh / HMR doesn't fork
// it: after a module reload the new module instance reads/writes the same
// shared state the still-installed listeners are notifying. Without this,
// dev would lose the fallback wiring on every save.

const SHARED_KEY = '__webglFatalState'

const fallbackSharedState = {
  failed: false,
  subscribers: new Set(),
  listenersInstalled: false,
}

function getSharedState() {
  if (typeof window === 'undefined') return fallbackSharedState
  if (!window[SHARED_KEY]) {
    window[SHARED_KEY] = {
      failed: false,
      subscribers: new Set(),
      listenersInstalled: false,
    }
  }
  return window[SHARED_KEY]
}

const LUMA_ERROR_PATTERNS = [
  /@luma\.gl/i,
  /_initDevice/,
  /createBrowserContext/,
  /getMaxDrawingBufferSize/,
  /maxTextureDimension2D/,
  /reading 'luma'/i,
  /_handleResize/,
]

function looksLikeLumaFailure(reasonOrError) {
  if (!reasonOrError) return false
  const stack = typeof reasonOrError === 'string' ? reasonOrError : reasonOrError.stack || reasonOrError.message || ''
  if (!stack) return false
  return LUMA_ERROR_PATTERNS.some(pattern => pattern.test(stack))
}

function markWebGLFatallyFailed(reason, description = 'luma.gl async failure detected') {
  const state = getSharedState()
  if (state.failed) return
  state.failed = true
  // eslint-disable-next-line no-console
  console.warn(`[useWebGLSupport] ${description}; disabling WebGL maps for the session:`, reason)
  state.subscribers.forEach(subscriber => {
    try {
      subscriber()
    } catch {
      // a subscriber that throws shouldn't block the rest from updating
    }
  })
}

function installAsyncFailureListeners() {
  if (typeof window === 'undefined') return
  const state = getSharedState()
  if (state.listenersInstalled) return
  state.listenersInstalled = true

  window.addEventListener('unhandledrejection', event => {
    if (looksLikeLumaFailure(event?.reason)) markWebGLFatallyFailed(event.reason)
  })

  window.addEventListener('error', event => {
    if (looksLikeLumaFailure(event?.error || event?.message)) markWebGLFatallyFailed(event.error || event.message)
  })
}

installAsyncFailureListeners()

/**
 * Test-only hook: lets unit tests trigger the same code path that the
 * window listeners do without dispatching real DOM events.
 */
export function triggerWebGLFatalForTests(reason) {
  markWebGLFatallyFailed(reason)
}

/**
 * Reported by the post-render canary (`blankRenderVerifier`) when the device
 * renders frames without errors but composites blank output (CS-2844).
 * Routed through the same fatal flag as async luma.gl failures so every
 * consumer falls back to DOM markers for the rest of the session.
 */
export function reportWebGLBlankRender(componentName) {
  markWebGLFatallyFailed(`[${componentName}] deck.gl produced blank frames with markers in view`, 'blank WebGL render detected')
}

// ─── Synchronous capability probe ─────────────────────────────────────────

let cachedSupport = null
let cachedMaxTextureSize = null

function probeWebGL() {
  if (cachedSupport !== null) return
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    cachedSupport = false
    cachedMaxTextureSize = 0
    return
  }
  try {
    const canvas = document.createElement('canvas')
    // deck.gl 9 / luma.gl 9 require WebGL2. The basic-support flag must reflect
    // that — devices reporting only WebGL1 will crash async during _initDevice.
    const gl =
      (window.WebGL2RenderingContext && canvas.getContext('webgl2')) ||
      (window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')))
    if (gl) {
      cachedSupport = true
      const reported = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 0
      cachedMaxTextureSize = reported > 0 ? reported : 2048
    } else {
      cachedSupport = false
      cachedMaxTextureSize = 0
    }
  } catch {
    cachedSupport = false
    cachedMaxTextureSize = 0
  }
}

/**
 * Returns true when the browser can power deck.gl's WebGL surfaces. Returns
 * false either when the device never had support, OR when an async luma.gl
 * failure was detected at runtime (see `triggerWebGLFatalForTests`).
 * Subscribes the calling component so it re-renders when the fatal flag flips.
 *
 * Detection runs once and is cached globally — safe to call in many components.
 */
export function useWebGLSupport() {
  const [supported] = useState(() => {
    probeWebGL()
    return cachedSupport
  })
  const [, force] = useReducer(x => x + 1, 0)

  useEffect(() => {
    const state = getSharedState()
    state.subscribers.add(force)
    return () => {
      state.subscribers.delete(force)
    }
  }, [])

  return supported && !getSharedState().failed
}

/**
 * Returns the GPU's reported MAX_TEXTURE_SIZE — the largest texture dimension
 * the device will actually upload. Older / integrated GPUs report 2048;
 * modern hardware reports 4096–16384. Returns 0 when WebGL is unsupported.
 */
export function getMaxTextureSize() {
  probeWebGL()
  return cachedMaxTextureSize ?? 0
}

// ─── IconLayer pipeline round-trip probe ──────────────────────────────────

let cachedCanRenderIconLayer = null

const PROBE_VS = `
  attribute vec2 p;
  varying vec2 uv;
  void main() {
    uv = p * 0.5 + 0.5;
    gl_Position = vec4(p, 0.0, 1.0);
  }
`
const PROBE_FS = `
  precision mediump float;
  uniform sampler2D s;
  varying vec2 uv;
  void main() { gl_FragColor = texture2D(s, uv); }
`

function probeIconLayerPipeline() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false

  const src = document.createElement('canvas')
  src.width = 4
  src.height = 4
  const ctx2d = src.getContext('2d')
  if (!ctx2d) return false
  ctx2d.fillStyle = '#ff0000'
  ctx2d.fillRect(0, 0, 4, 4)

  const glCanvas = document.createElement('canvas')
  glCanvas.width = 1
  glCanvas.height = 1
  // deck.gl 9 / luma.gl 9 internally require WebGL2 — the webgl1 fallback
  // path masks the exact bug we're trying to catch (device init succeeds on
  // webgl1 but crashes when luma reads webgl2-only limits like
  // maxTextureDimension2D from the adapter).
  const gl = glCanvas.getContext('webgl2')
  if (!gl) return false

  try {
    // luma.gl reads MAX_TEXTURE_SIZE during device init and again on every
    // ResizeObserver callback. If the driver returns 0 / null / throws, those
    // async paths produce the "Cannot read properties of undefined (reading
    // 'maxTextureDimension2D')" crash. Keep the call inside the try block so a
    // throwing driver returns false cleanly instead of taking out the render.
    const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE)
    if (!Number.isFinite(maxTextureSize) || maxTextureSize <= 0) return false

    const tex = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, tex)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src)
    if (gl.getError() !== gl.NO_ERROR) return false

    const fbTex = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, fbTex)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)

    const fb = gl.createFramebuffer()
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb)
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, fbTex, 0)
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) return false

    const compile = (type, source) => {
      const shader = gl.createShader(type)
      gl.shaderSource(shader, source)
      gl.compileShader(shader)
      return gl.getShaderParameter(shader, gl.COMPILE_STATUS) ? shader : null
    }
    const vs = compile(gl.VERTEX_SHADER, PROBE_VS)
    const fs = compile(gl.FRAGMENT_SHADER, PROBE_FS)
    if (!vs || !fs) return false

    const prog = gl.createProgram()
    gl.attachShader(prog, vs)
    gl.attachShader(prog, fs)
    gl.linkProgram(prog)
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return false
    gl.useProgram(prog)

    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, tex)
    gl.uniform1i(gl.getUniformLocation(prog, 's'), 0)

    const buf = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buf)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)
    const loc = gl.getAttribLocation(prog, 'p')
    gl.enableVertexAttribArray(loc)
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)

    gl.viewport(0, 0, 1, 1)
    gl.clearColor(0, 0, 0, 0)
    gl.clear(gl.COLOR_BUFFER_BIT)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

    const px = new Uint8Array(4)
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px)

    return px[0] > 200 && px[1] < 50 && px[2] < 50
  } catch {
    return false
  }
}

/**
 * Synthetic round-trip probe: requires WebGL2, validates MAX_TEXTURE_SIZE,
 * uploads a small canvas as a texture, samples it through a minimal shader,
 * and reads back the pixel. Returns false when the device's IconLayer pipeline
 * can't actually render canvas-source textures (drivers that fail silently,
 * missing extensions, software fallbacks that blank textures, etc.).
 *
 * Cached after first call. Safe to invoke multiple times.
 */
export function getCanRenderIconLayer() {
  if (getSharedState().failed) return false
  if (cachedCanRenderIconLayer !== null) return cachedCanRenderIconLayer
  cachedCanRenderIconLayer = probeIconLayerPipeline()
  return cachedCanRenderIconLayer
}
